import "./TeamPageV2.scss";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Input, Skeleton, Tabs, TabsProps } from "antd";
import { useRecoilState } from "recoil";
import { memberState } from "../../../../state/atoms";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MemberDto, MemberDtoRoleEnum, MemberLocationPermissionDto } from "../../../../generated/dashboard-client";
import AccountsApi from "../../../../api/AccountsApi";
import { UsersTable, UsersTableType } from "./UsersTable";

import UsersApi from "../../../../api/UsersApi";
import { success } from "../../../../service/MessageService";
import { handleServerError } from "../../../../util/ErrorHandler";
import { useLocation, useNavigate } from "react-router-dom";
import { env } from "../../../../env/env";
import { debounce } from "lodash";
import MembersApi from "../../../../api/MembersApi";
import { isDesignSubscription, isProSubscription } from "../../../../service/BillingService";
import UserGroupAddIcon from "../../../icons/UserGroupAddIcon";
import ControlIcon from "../../../icons/ControlIcon";
import PermissionPresetsApi from "../../../../api/PermissionPresetsApi";

const { Search } = Input;

export function TeamPageV2() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [member] = useRecoilState(memberState);
  const [users, setUsers] = useState([] as MemberDto[]);
  const [isFreeAccount, setIsFreeAccount] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [searchKey, setSearchKey] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userPermissions, setUserPermissions] = useState<Record<string, MemberLocationPermissionDto[]>>({});

  const debouncedSetSearchKey = useMemo(() => debounce((value: string) => {
    setSearchKey((value ?? "").trim());
    setCurrentPage(1);
    setPageSize(10);
  }, 300), []);

  const fetchData = useCallback(() => {
    if (!member.id || !member.plan || !member.internalId) return;
    setLoading(true);
    console.log("=> fetching data for currentPage=", currentPage, " pageSize=", pageSize, ", searchKey=", searchKey);

    let status = "active";
    if (activeTabKey === "2") {
      status = "pending";
    } else if (activeTabKey === "3") {
      status = "inactive";
    }

    AccountsApi.getUsersDetails(member.id, status, currentPage, pageSize, searchKey).then((resp) => {
      setLoading(false);
      const owner = resp.data.owner ?? {};
      const found = resp.data.users ?? [];

      let includeOwner = currentPage === 1 && activeTabKey === "1";
      if (searchKey) {
        includeOwner = owner.email!.toLowerCase().includes(searchKey.toLowerCase()) || owner.name!.toLowerCase().includes(searchKey.toLowerCase());
      }

      if (includeOwner) {
        setUsers([{
          id: owner.id ?? "owner",
          email: owner.email ?? "",
          name: owner.name ?? "",
          role: "Owner",
          invitationStatus: "Active"
        } as MemberDto, ...found]);
      } else {
        setUsers([...found]);
      }

      const total = resp.data.total ?? 0;
      setTotalUsers(total);

      if (!isProSubscription(member) && !member.diningRDIntegration) setIsFreeAccount(true);

      const userRoleIds = found.filter(u => u.role === MemberDtoRoleEnum.User && u.id).map(u => u.id!);
      if (userRoleIds.length > 0) {
        MembersApi.getBatchTeamLocationPermissions(userRoleIds).then(r => {
          setUserPermissions(r.data);
        }).catch(() => {
          setUserPermissions({});
        });
      } else {
        setUserPermissions({});
      }
    }).catch(() => {
      setLoading(false);
    });
  }, [member, currentPage, pageSize, searchKey, activeTabKey]);

  useEffect(fetchData, [fetchData]);

  useEffect(() => {
    if (location.search.indexOf("pending") > -1) {
      setActiveTabKey("2");
    }
  }, [location]);

  function onMenuOptionClick(option: string, user: MemberDto) {
    if (option === "edit-locations") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/locations`);
    } else if (option === "edit-permissions") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/permissions`);
    } else if (option === "resend-invite") {
      setLoading(true);
      UsersApi.sendInvitationEmail(user.id || "").then(() => {
        success("Invitation sent!");
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "cancel-invite") {
      MembersApi.deleteUserTeamLocationPermissions(user.id!).then(() => { });
      UsersApi.revokeInvitation(user.id!).then(() => {
        success("Invitation revoked!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "remove-access") {
      setLoading(true);
      UsersApi.revokeUser(user.id!).then(() => {
        success("Access removed!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "activate-account") {
      if (user.role === MemberDtoRoleEnum.Admin) {
        MembersApi.updateUserLocationPermissions(user.id ?? "", {
          activate: true,
          locations: {}
        }).then(() => {
          success("Updated!");
          setUsers(users.filter((it) => it.id !== user.id));
        }).catch((err) => {
          handleServerError(err);
        });
      } else {
        navigate(env.dashboardPath + `/account/team/user/${user.id}/locations?activate=y`);
      }
    } else if (option === "permanently-remove") {
      setLoading(true);
      UsersApi.removeUser(user.id!).then(() => {
        success("User removed!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => {
        setLoading(false);
      });
    }
  }

  function sortUsers(key: string) {
    setUsers([...users].sort((a: MemberDto, b: MemberDto) => {
      if (!key) return 0;
      return key === "asc" ? (a.name ?? "").localeCompare(b.name ?? "") : (b.name ?? "").localeCompare(a.name ?? "")
    }))
  }

  function changeRole(member: MemberDto, role: MemberDtoRoleEnum) {
    if (member.role === role) return;

    MembersApi.updateRole(member.id!, role).then(() => {
      fetchData();
      if (role === MemberDtoRoleEnum.User) {
        navigate(env.dashboardPath + `/account/team/user/${member.id}/locations`);
      }
    })
  }

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Active Users",
      children: <UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        onChangeRoleClick={changeRole}
        type={UsersTableType.Active}
        users={users}
        userPermissions={userPermissions}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      />
    },
    {
      key: "2",
      label: "Pending Invitations",
      children: <UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        onChangeRoleClick={changeRole}
        type={UsersTableType.Pending}
        users={users}
        userPermissions={userPermissions}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      />
    },
    {
      key: "3",
      label: "Inactive Users",
      children: <UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        onChangeRoleClick={changeRole}
        type={UsersTableType.Inactive}
        users={users}
        userPermissions={userPermissions}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      />
    },
  ];

  // Free/Design-plan accounts must upgrade to Pro before managing their team. Returns true when the
  // upgrade modal was shown (so the caller should stop), false when the account may proceed.
  function requireProUpgrade(): boolean {
    if (!isFreeAccount) return false;
    if (isDesignSubscription(member)) {
      window.MHM.ProModal.switchToProWithOverview({
        memberId: member.id,
        onSuccess: () => window.location.reload()
      });
    } else {
      window.MHM.ProModal.upgrade({
        memberId: member.id,
        onSuccess: () => window.location.reload()
      });
    }
    return true;
  }

  function onNewUserClick() {
    if (requireProUpgrade()) return;
    navigate(env.dashboardPath + "/account/team/new");
  }

  function onBulkInviteClick() {
    if (requireProUpgrade()) return;
    const teamId = member.teamId ?? "";
    if (!teamId) {
      navigate(env.dashboardPath + "/account/team/bulk-invite");
      return;
    }
    // Bulk invite relies on presets to assign permissions; if none exist yet,
    // send the user to create their first preset instead.
    PermissionPresetsApi.getPresets(teamId).then((resp) => {
      if ((resp.data ?? []).length === 0) {
        navigate(env.dashboardPath + "/account/team/permission-presets");
      } else {
        navigate(env.dashboardPath + "/account/team/bulk-invite");
      }
    }).catch(() => {
      navigate(env.dashboardPath + "/account/team/bulk-invite");
    });
  }

  function onPermissionPresetsClick() {
    if (requireProUpgrade()) return;
    navigate(env.dashboardPath + "/account/team/permission-presets");
  }

  function onSearchKeyUp(target: HTMLInputElement) {
    debouncedSetSearchKey(target.value);
  }

  return (
    <div className={"content team-page-v2"}>
      <div className={"banner"}>
        <h3>My Team</h3>
        <h5>Work better together</h5>
        <div className={"users"}>
          <div className={"circle a"}><UserOutlined /></div>
          <div className={"circle b"}><UserOutlined /></div>
          <div className={"circle c"}><UserOutlined /></div>
        </div>
      </div>

      <header>
        <Button type={"primary"} className={"new"} onClick={onNewUserClick}>+ New User</Button>
        <Button className={"secondary"} icon={<UserGroupAddIcon />} onClick={onBulkInviteClick}>Bulk Invite</Button>
        <Button className={"secondary"} icon={<ControlIcon />} onClick={onPermissionPresetsClick}>Manage Permission Presets</Button>
        {(users.length > 0 || searchKey.length > 0) &&
          <Search className={"search"} onKeyUp={ev => onSearchKeyUp(ev.target as HTMLInputElement)}
            placeholder="Search users" prefix={<SearchOutlined />} addonAfter={<></>} />}
      </header>

      {loading && <Skeleton />}

      {!loading && <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={items} />}
    </div>
  );
}
